import { type Control, Controller } from 'react-hook-form';

import SecureIconSZ from 'assets/icons/sz/secure-icon.svg';
import { useTranslate, useValidate, type CardOptionType } from 'hooks';
import { useAppSelector } from 'store/hooks';
import { renderBankIcon, renderCardTypeIcon } from 'utils/render-icon';
import { Input } from 'views/components/sz/ui/input';

export type CardFieldsType = {
  pan: '';
  validity: '';
  cvv: '';
  email: '';
};

type CardDetailsType = {
  control: Control<CardFieldsType, any>;
  selectedCard: CardOptionType;
};

const CardDetails = ({ control, selectedCard }: CardDetailsType) => {
  const { paymentData, amount, isPayment } = useAppSelector((state) => state.payment);
  const { t } = useTranslate();
  const { validateCardDate, validateCardNumber } = useValidate();

  return paymentData ? (
    <div className="flex flex-col gap-4 md:gap-6">
      <Controller
        name="pan"
        control={control}
        rules={
          selectedCard.newCard
            ? {
                required: true,
                validate: validateCardNumber,
              }
            : undefined
        }
        render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
          <Input
            label={t('card_number')}
            placeholder={selectedCard.newCard ? '0000 0000 0000 0000' : selectedCard.maskedPan}
            LeftIcon={
              !selectedCard.newCard && selectedCard.bankBin
                ? renderBankIcon(selectedCard.bankBin)
                : undefined
            }
            RightIcon={
              !selectedCard.newCard && selectedCard.bankBin
                ? renderCardTypeIcon(selectedCard.bankBin)
                : undefined
            }
            mask=" "
            name="cardNumber"
            format="#### #### #### ####"
            inputClassName="w-full"
            value={value}
            ref={ref}
            onChange={onChange}
            type="tel"
            error={error?.message}
            disabled={paymentData.commission > amount || !selectedCard.newCard}
          />
        )}
      />
      {isPayment ? (
        <div className="flex gap-6 flex-1 lg:mt-0">
          <Controller
            name="validity"
            control={control}
            rules={
              selectedCard.newCard
                ? {
                    required: true,
                    validate: validateCardDate,
                  }
                : undefined
            }
            render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
              <Input
                label={t('expired_date')}
                name="expiryDate"
                placeholder={selectedCard.newCard ? 'ММ/ГГ' : '**/**'}
                inputClassName="w-full"
                mask=" "
                format="##/##"
                value={value}
                onChange={onChange}
                ref={ref}
                type="tel"
                error={error?.message}
                disabled={paymentData.commission > amount || !selectedCard.newCard}
              />
            )}
          />
          <Controller
            name="cvv"
            control={control}
            rules={
              selectedCard.newCard
                ? {
                    required: true,
                    pattern: {
                      value: /^\d{3,4}$/,
                      message: t('wrong_cvv_code'),
                    },
                  }
                : undefined
            }
            render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
              <Input
                name="cvv"
                label={t('cvv_code')}
                mask=""
                placeholder={selectedCard.newCard ? 'CVC' : '***'}
                inputClassName="w-full"
                format="###"
                value={value}
                onChange={onChange}
                ref={ref}
                type="password"
                error={error?.message}
                disabled={paymentData.commission > amount || !selectedCard.newCard}
              />
            )}
          />
        </div>
      ) : null}
      <div className="flex items-center gap-2">
        <img src={SecureIconSZ} width={20} height={20} alt="secure" />
        <span className="text-sm leading-[12px] text-sz-gray-500">
          Безопасная передача банковских данных
        </span>
      </div>
    </div>
  ) : null;
};

export { CardDetails };
